'use client'

import { type SVGProps, useState } from 'react'
import { IconLoader2, IconLock } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { useTranslations } from 'next-intl'

interface AuthFormProps {
  onSuccess?: () => void
  compact?: boolean
  redirectTo?: string | null
}

function GoogleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        fill="#4285F4"
        d="M23.52 12.273c0-.851-.076-1.67-.218-2.455H12v4.645h6.48c-.279 1.5-1.125 2.773-2.392 3.622v3.01h3.868c2.265-2.086 3.564-5.158 3.564-8.822z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.951-1.073 7.934-2.905l-3.868-3.01c-1.073.72-2.45 1.148-4.066 1.148-3.127 0-5.771-2.11-6.715-4.952H1.29v3.115C3.266 21.408 7.31 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.285 14.281A7.214 7.214 0 0 1 4.9 12c0-.79.136-1.56.387-2.281V6.604H1.29A11.965 11.965 0 0 0 0 12c0 1.95.466 3.793 1.29 5.396l3.995-3.115z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.76 0 3.34.605 4.584 1.793l3.435-3.435C17.947.985 15.236 0 12 0 7.31 0 3.266 2.592 1.29 6.604l3.995 3.115C6.229 6.86 8.873 4.75 12 4.75z"
      />
    </svg>
  )
}

export function AuthForm({
  onSuccess,
  compact = false,
  redirectTo
}: AuthFormProps) {
  const t = useTranslations('auth')
  const { signInWithGoogle } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [oauthLoading, setOauthLoading] = useState(false)
  const [authMode, setAuthMode] = useState<'sign-in' | 'sign-up'>('sign-in')
  const [status, setStatus] = useState<{
    intent: 'success' | 'error'
    message: string
  } | null>(null)

  // Handle email + password auth
  const handleEmailPasswordAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setStatus(null)

    const action =
      authMode === 'sign-in'
        ? supabase.auth.signInWithPassword({ email, password })
        : supabase.auth.signUp({ email, password })

    const { data, error } = await action
    if (error) {
      setStatus({ intent: 'error', message: error.message })
    } else {
      setStatus({
        intent: 'success',
        message:
          authMode === 'sign-in' ? t('signInSuccess') : t('signUpSuccess')
      })
      if (authMode === 'sign-up') {
        setEmail('')
        setPassword('')
      }
      // Call onSuccess after a short delay to allow state to update
      if (onSuccess && (authMode === 'sign-in' || data.session)) {
        setTimeout(() => {
          onSuccess()
        }, 500)
      }
    }
    setLoading(false)
  }

  const handleGoogle = async () => {
    try {
      setOauthLoading(true)
      setStatus(null)
      await signInWithGoogle(redirectTo)
      // Google OAuth redirects, so onSuccess will be called after redirect
      if (onSuccess) {
        setTimeout(() => {
          onSuccess()
        }, 100)
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('googleSignInError')
      setStatus({ intent: 'error', message })
    } finally {
      setOauthLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {status && (
        <div
          role="status"
          aria-live="polite"
          className={`rounded-lg border px-4 py-3 text-sm ${
            status.intent === 'success'
              ? 'border-emerald-300/50 bg-emerald-50 text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-100'
              : 'border-destructive/40 bg-destructive/5 text-destructive'
          }`}
        >
          {status.message}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={handleGoogle}
        disabled={oauthLoading || loading}
      >
        {oauthLoading ? (
          <IconLoader2 className="h-4 w-4 animate-spin" />
        ) : (
          <GoogleIcon className="h-4 w-4" />
        )}
        {t('continueWithGoogle')}
      </Button>

      <Separator className="bg-border/70" />

      <form onSubmit={handleEmailPasswordAuth} className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="dialog-email">{t('emailAddress')}</Label>
          <Input
            id="dialog-email"
            type="email"
            placeholder={t('emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading || oauthLoading}
            autoComplete="email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="dialog-password">{t('password')}</Label>
          <Input
            id="dialog-password"
            type="password"
            placeholder={t('passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading || oauthLoading}
            autoComplete={
              authMode === 'sign-in' ? 'current-password' : 'new-password'
            }
          />
        </div>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <IconLoader2 className="h-4 w-4 animate-spin" />
              {authMode === 'sign-in' ? t('signingIn') : t('creatingAccount')}
            </>
          ) : (
            <>
              <IconLock className="h-4 w-4" />
              {authMode === 'sign-in' ? t('signIn') : t('createAccount')}
            </>
          )}
        </Button>
      </form>
      <button
        type="button"
        onClick={() =>
          setAuthMode((mode) => (mode === 'sign-in' ? 'sign-up' : 'sign-in'))
        }
        className="text-muted-foreground text-xs underline-offset-4 hover:underline"
      >
        {authMode === 'sign-in' ? t('noAccount') : t('hasAccount')}
      </button>
      {!compact && (
        <p className="text-muted-foreground text-xs">{t('passwordInfo')}</p>
      )}
    </div>
  )
}
